import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearAccountInvoiceReminder,
  getAccountInvoiceReminder,
  setAccountInvoiceReminder,
} from '../api';
import { ApiError } from '@/lib/api';

export const invoiceReminderQueryKey = (accountId: string) => ['account-invoice-reminder', accountId] as const;

export function useAccountInvoiceReminder(accountId: string) {
  return useQuery({
    queryKey: invoiceReminderQueryKey(accountId),
    queryFn: () => getAccountInvoiceReminder(accountId),
    staleTime: 60 * 1000,
    // A 403 is a permission answer, not a transient failure.
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 403) && failureCount < 1,
  });
}

export function useSetAccountInvoiceReminder(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (overdueSince: string) => setAccountInvoiceReminder(accountId, overdueSince),
    onSuccess: (state) => queryClient.setQueryData(invoiceReminderQueryKey(accountId), state),
  });
}

export function useClearAccountInvoiceReminder(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => clearAccountInvoiceReminder(accountId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invoiceReminderQueryKey(accountId) }),
  });
}
